import styles from 'css/src/components/common/ui/progress-bar.module.scss';

import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

// when the progress bar first loads, does an animation play showing the bar filling/emptying
export enum PROGRESS_BAR_INITIAL_ANIMATION_STYLES {
    FILL = 'fill',
    EMPTY = 'empty',
    NONE = 'none',
}

export enum PROGRESS_BAR_VARIANTS {
    DEFAULT = 'default',
    MINIMAL = 'minimal',
}

type Props = {
    current: number;
    total: number;
    variant?: PROGRESS_BAR_VARIANTS;
    initialAnimationStyle?: PROGRESS_BAR_INITIAL_ANIMATION_STYLES;
    className?: string;
};

const ProgressBar = ({
    current,
    total,
    variant = PROGRESS_BAR_VARIANTS.DEFAULT,
    initialAnimationStyle = PROGRESS_BAR_INITIAL_ANIMATION_STYLES.NONE,
    className,
}: Props) => {
    const hasInitialAnimation =
        initialAnimationStyle !== PROGRESS_BAR_INITIAL_ANIMATION_STYLES.NONE;
    let current_progress_percent = 0;
    if (total > 0 && current >= 0) {
        current_progress_percent = Math.min((current * 100.0) / total, 100);
    }

    const animationStyleInitialFillPercentMap = {
        [PROGRESS_BAR_INITIAL_ANIMATION_STYLES.FILL]: 0,
        [PROGRESS_BAR_INITIAL_ANIMATION_STYLES.EMPTY]: 100,
        [PROGRESS_BAR_INITIAL_ANIMATION_STYLES.NONE]: current_progress_percent,
    };

    const [displayedFillPercentage, setDisplayedFillPercentage] = useState(
        animationStyleInitialFillPercentMap[initialAnimationStyle],
    );

    useEffect(() => {
        // before playing initial animation, slight delay effect
        const shouldDelay = hasInitialAnimation && [0, 100].includes(displayedFillPercentage);
        setTimeout(
            () => {
                setDisplayedFillPercentage(current_progress_percent);
            },
            shouldDelay ? 100 : 0,
        );
    }, [current_progress_percent, hasInitialAnimation, displayedFillPercentage]);

    const style_attr = {
        width: displayedFillPercentage.toString() + '%',
    };
    if (current < 0) {
        return null;
    }
    return (
        <div
            className={classNames(
                'progress-bar progress',
                styles['full-width-progress'],
                className,
                {
                    [styles['grey']]: variant === PROGRESS_BAR_VARIANTS.DEFAULT,
                    [styles['progress-bar--minimal']]: variant === PROGRESS_BAR_VARIANTS.MINIMAL,
                },
            )}
        >
            <div
                className={classNames('progress-bar', {
                    'progress-bar-striped progress-bar-animated':
                        variant === PROGRESS_BAR_VARIANTS.DEFAULT,
                    [styles['progress-bar--minimal__fill']]:
                        variant === PROGRESS_BAR_VARIANTS.MINIMAL,
                    [styles['progress-bar__fill--animated']]: hasInitialAnimation,
                })}
                role="progressbar"
                aria-valuenow={current_progress_percent}
                aria-valuemin={0}
                aria-valuemax={100}
                style={style_attr}
            >
                <span className="visually-hidden">{current_progress_percent}% Complete</span>
            </div>
        </div>
    );
};

export default ProgressBar;
